// WallApp.jsx - Main wall container component
import anime from 'animejs';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { WallCTA } from './WallCTA.jsx';
import { WallLogo } from './WallLogo.jsx';
import { WallMessage, WallMessageMemo } from './WallMessage.jsx';
import { WallPartner } from './WallPartner.jsx';
import { WallUser, WallUserMemo } from './WallUser.jsx';
import { WallBG, WallBGMemo } from './WallBG.jsx';

export const WallApp = ({
  wallData = {},
  renderEmblem = null,
  renderDetails = null,
  showWallLogo = false,
}) => {
  const wallMaxH = wallData?.wallSettings?.wallMaxH || '';
  const isLandscapeOnly = wallData?.isLandscapeOnly || false;
  const isWallOnly = wallData?.isWallOnly || false;
  const animationRef = useRef(null);

  const refWall = useRef(null);

  // Delay WallMessage mount by 500ms after WallBG mounts.
  // useFitText measures the container immediately — if the background
  // image hasn't loaded yet, the container has 0 height and fitText
  // calculates a bogus font size (1500%). The 500ms delay gives the
  // background enough time to load and establish the container height.
  const [showMessage, setShowMessage] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setShowMessage(true), 10);
    return () => clearTimeout(timer);
  }, []);

  const wallLogo = wallData?.wallLogo?.show ?? true;
  const wallCTA = wallData?.wallSettings?.wallCTA?.ctaShow ?? true;

  const isOnAir = wallData?.pipe?.isOnAir ?? false;

  useEffect(() => {
    animationRef.current = anime({
      targets: refWall.current,
      opacity: 0,
      duration: 0,
      complete: () => {
        anime({
          targets: refWall.current,
          opacity: 1,
          easing: 'easeOutSine',
          duration: 1000,
        });
      },
    });

    return () => {
      if (animationRef.current) {
        animationRef.current.pause();
      }
    };
  }, [isLandscapeOnly]);

  const wallContainerStyle = isLandscapeOnly
    ? ''
    : isWallOnly
      ? ''
      : 'lg:rounded-b-lg';

  const wallUser = useMemo(() => {
    return <WallUser wallData={wallData} renderEmblem={renderEmblem} />;
  }, [
    wallData?.wallMessage?.postItem?.userName,
    wallData?.wallMessage?.postItem?.type,
    renderEmblem,
  ]);

  const wallAspectRatio = wallData?.wallAspectRatio || 'w-dvw h-dvh';

  return (
    <div
      id="wallContainer"
      ref={refWall}
      className={[
        'relative overflow-hidden opacity-0 shadow-xl shadow-[color-mix(in_srgb,var(--color-customer)100%,transparent)]/25 select-none',
        wallContainerStyle,
        // wallAspectRatio,
      ].join(' ')}
    >
      <WallBGMemo wallData={wallData} />

      {/* {showWallLogo && wallLogo && <WallLogo wallData={wallData} />} */}

      {wallData?.wallPartner?.partnerLogo && (
        <WallPartner wallData={wallData} />
      )}

      {isOnAir && (
        <>
          {showMessage && <WallMessageMemo wallData={wallData} />}

          <WallUserMemo wallData={wallData} renderEmblem={renderEmblem} />

          {!isWallOnly && renderDetails && (
            <div id="wallDetailsActions">{renderDetails(wallData)}</div>
          )}
        </>
      )}

      {isWallOnly && <WallCTA wallData={wallData} />}
    </div>
  );
};
